
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '@/contexts/LanguageContext';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import ProductsSection from '@/components/ProductsSection';
import SolutionsSection from '@/components/SolutionsSection';
import AboutSection from '@/components/AboutSection';
import ContactSection from '@/components/ContactSection';
import Footer from '@/components/Footer';

const Index = () => {
  const { language } = useLanguage();
  
  // Language-specific content for SEO
  const seo = {
    en: {
      title: 'Attaboy Intelligence | AI-Powered Tools for Business Transformation',
      description: 'AI-powered tools and solutions for a smarter future. Transform your business with Attaboy Intelligence - making artificial intelligence radically accessible.',
      keywords: 'ai tools, artificial intelligence, business automation, AI solutions, machine learning',
      canonicalUrl: 'https://attaboyintelligence.com/',
      alternateUrl: 'https://attaboyintelligenceartificielle.com/'
    },
    fr: {
      title: 'Attaboy Intelligence Artificielle | Outils IA pour la Transformation des Entreprises',
      description: 'Des outils et solutions propulsés par l\'IA pour un avenir plus intelligent. Transformez votre entreprise avec Attaboy Intelligence Artificielle - rendant l\'intelligence artificielle radicalement accessible.',
      keywords: 'outils ia, intelligence artificielle, automatisation d\'entreprise, solutions IA, apprentissage automatique',
      canonicalUrl: 'https://attaboyintelligenceartificielle.com/',
      alternateUrl: 'https://attaboyintelligence.com/'
    }
  };
  
  const currentSeo = language === 'en' ? seo.en : seo.fr;

  useEffect(() => {
    try {
      // Scroll to top when component mounts
      window.scrollTo(0, 0);
      
      // Check if running on Cloudflare
      if (window.location.hostname.includes('pages.dev')) {
        console.log('Running on Cloudflare Pages');
      }
    } catch (error) {
      console.error('Error in Index component:', error);
    }
  }, []);

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <meta name="keywords" content={currentSeo.keywords} />
        
        {/* Canonical and alternate language URLs */}
        <link rel="canonical" href={currentSeo.canonicalUrl} />
        <link rel="alternate" hrefLang={language === 'en' ? 'fr' : 'en'} href={currentSeo.alternateUrl} />
        <link rel="alternate" hrefLang="x-default" href="https://attaboyintelligence.com/" />
        
        {/* Open Graph metadata */}
        <meta property="og:title" content={currentSeo.title} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentSeo.canonicalUrl} />
        <meta property="og:locale" content={language === 'en' ? 'en_US' : 'fr_FR'} />
        <meta property="og:site_name" content={language === 'en' ? 'Attaboy Intelligence' : 'Attaboy Intelligence Artificielle'} />
        
        {/* Twitter Card metadata */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentSeo.title} />
        <meta name="twitter:description" content={currentSeo.description} />
        
        {/* Structured data for Organization */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "${language === 'en' ? 'Attaboy Intelligence' : 'Attaboy Intelligence Artificielle'}",
              "description": "${currentSeo.description}",
              "url": "${currentSeo.canonicalUrl}",
              "logo": "${currentSeo.canonicalUrl}logo.png",
              "sameAs": [
                "https://twitter.com/attaboy_ai",
                "https://linkedin.com/company/attaboy-intelligence"
              ]
            }
          `}
        </script>
      </Helmet>
      
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main>
          <Hero />
          <ProductsSection />
          <SolutionsSection />
          <AboutSection />
          <ContactSection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Index;
